/**
 * MedPass Safety Alerts Component
 * 
 * Displays safety alerts and warnings for the MedPass workflow,
 * including drug interactions, allergies, and contraindications.
 */

import React from 'react';
import {
    Alert,
    List,
    Badge,
    Card,
    Row,
    Col,
    Space,
    Tag,
    Divider,
    Typography,
    Empty
} from 'antd';
import {
    ExclamationCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const MedPassSafetyAlerts = ({ alerts = [] }) => {
    // Group alerts by severity
    const groupedAlerts = alerts.reduce((groups, alert) => {
        const severity = alert.severity || 'minor';
        if (!groups[severity]) {
            groups[severity] = [];
        }
        groups[severity].push(alert);
        return groups;
    }, {});

    // Severity configurations
    const severityConfig = {
        contraindicated: {
            color: 'red',
            icon: <CloseCircleOutlined />,
            label: 'Contraindicated',
            alertType: 'error'
        },
        major: {
            color: 'orange',
            icon: <ExclamationCircleOutlined />,
            label: 'Major',
            alertType: 'warning'
        },
        moderate: {
            color: 'gold',
            icon: <WarningOutlined />,
            label: 'Moderate',
            alertType: 'warning'
        },
        minor: {
            color: 'blue',
            icon: <InfoCircleOutlined />,
            label: 'Minor',
            alertType: 'info'
        }
    };

    // Alert type configurations
    const alertTypeConfig = {
        drug_interaction: {
            title: 'Drug Interaction',
            color: 'red',
            icon: '⚠️'
        },
        allergy: {
            title: 'Allergy Alert',
            color: 'volcano',
            icon: '🚨'
        },
        age_verification: {
            title: 'Age Verification',
            color: 'orange',
            icon: '👶'
        },
        geriatric_consideration: {
            title: 'Geriatric Consideration',
            color: 'gold',
            icon: '👴'
        },
        contraindication: {
            title: 'Contraindication',
            color: 'red',
            icon: '🚫'
        },
        dose_adjustment: {
            title: 'Dose Adjustment',
            color: 'blue',
            icon: '💊'
        },
        monitoring_required: {
            title: 'Monitoring Required',
            color: 'purple',
            icon: '📊'
        }
    };

    const renderAlertCard = (alert, index) => {
        const severityConf = severityConfig[alert.severity] || severityConfig.minor;
        const typeConf = alertTypeConfig[alert.alert_type] || { title: 'Alert', color: 'default', icon: '⚠️' };

        return (
            <Card
                key={index}
                size="small"
                style={{ marginBottom: 8 }}
                bodyStyle={{ padding: '12px 16px' }}
            >
                <Row gutter={[12, 8]} align="top">
                    <Col flex="auto">
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            {/* Alert Header */}
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Space>
                                        <span style={{ fontSize: '16px' }}>{typeConf.icon}</span>
                                        <Text strong>{typeConf.title}</Text>
                                        <Badge 
                                            status={severityConf.alertType === 'error' ? 'error' : 'warning'}
                                            text={severityConf.label}
                                        />
                                    </Space>
                                </Col>
                                <Col>
                                    {alert.requires_action && (
                                        <Tag color="red" size="small">
                                            Action Required
                                        </Tag>
                                    )}
                                </Col>
                            </Row>

                            {/* Alert Message */}
                            <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                                {alert.message}
                            </Paragraph>

                            {/* Recommendation */}
                            {alert.recommendation && (
                                <div style={{ 
                                    backgroundColor: '#f6ffed', 
                                    border: '1px solid #b7eb8f',
                                    borderRadius: '4px',
                                    padding: '8px',
                                    marginTop: '8px'
                                }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        <strong>Recommendation:</strong> {alert.recommendation}
                                    </Text>
                                </div>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    const renderSeveritySection = (severity, alerts) => {
        const config = severityConfig[severity] || severityConfig.minor;
        
        return (
            <div key={severity} style={{ marginBottom: 24 }}>
                <Row align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <Space>
                            {config.icon}
                            <Title level={5} style={{ margin: 0, color: config.color }}>
                                {config.label} Alerts ({alerts.length})
                            </Title>
                        </Space>
                    </Col>
                </Row>
                
                {alerts.map((alert, index) => renderAlertCard(alert, `${severity}-${index}`))}
            </div>
        );
    };

    // Summary statistics
    const totalAlerts = alerts.length;
    const criticalAlerts = alerts.filter(alert => alert.requires_action).length;
    const severityCounts = Object.keys(groupedAlerts).reduce((counts, severity) => {
        counts[severity] = groupedAlerts[severity].length;
        return counts;
    }, {});

    if (totalAlerts === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No safety alerts found"
            >
                <Text type="secondary">
                    All safety checks have passed successfully.
                </Text>
            </Empty>
        );
    }

    return (
        <div className="medpass-safety-alerts">
            {/* Summary */}
            <Alert
                message="Safety Alert Summary"
                description={
                    <Row gutter={16}>
                        <Col>
                            <Text strong>Total Alerts: </Text>
                            <Badge count={totalAlerts} style={{ backgroundColor: '#108ee9' }} />
                        </Col>
                        <Col>
                            <Text strong>Require Action: </Text>
                            <Badge count={criticalAlerts} style={{ backgroundColor: '#f50' }} />
                        </Col>
                        <Col>
                            <Space>
                                {Object.entries(severityCounts).map(([severity, count]) => {
                                    const config = severityConfig[severity] || severityConfig.minor;
                                    return (
                                        <Tag key={severity} color={config.color}>
                                            {config.label}: {count}
                                        </Tag>
                                    );
                                })}
                            </Space>
                        </Col>
                    </Row>
                }
                type={criticalAlerts > 0 ? 'error' : 'warning'}
                showIcon
                style={{ marginBottom: 16 }}
            />

            {/* Critical Actions Required */}
            {criticalAlerts > 0 && (
                <Alert
                    message="Immediate Actions Required"
                    description={
                        <div>
                            <Text>
                                {criticalAlerts} alert(s) require immediate attention before proceeding.
                            </Text>
                            <List
                                size="small"
                                dataSource={alerts.filter(alert => alert.requires_action)}
                                renderItem={(alert, index) => (
                                    <List.Item key={index}>
                                        <Space>
                                            <span>{alertTypeConfig[alert.alert_type]?.icon || '⚠️'}</span>
                                            <Text>{alert.message}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                                style={{ marginTop: 8 }}
                            />
                        </div>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Divider />

            {/* Alerts by Severity */}
            <div className="alerts-by-severity">
                {/* Render in order of severity */}
                {['contraindicated', 'major', 'moderate', 'minor'].map(severity => {
                    if (groupedAlerts[severity] && groupedAlerts[severity].length > 0) {
                        return renderSeveritySection(severity, groupedAlerts[severity]);
                    }
                    return null;
                })}
            </div>

            {/* Footer Information */}
            <Divider />
            <Alert
                message="Important Safety Information"
                description={
                    <div>
                        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                            • All safety alerts should be reviewed by a qualified healthcare provider before proceeding.
                        </Paragraph>
                        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                            • Alerts marked "Action Required" must be addressed before the prescription can be approved.
                        </Paragraph>
                        <Paragraph style={{ margin: 0, fontSize: '12px' }}>
                            • Contact the pharmacy or prescribing physician if you have questions about any alert.
                        </Paragraph>
                    </div>
                }
                type="info"
                showIcon={false}
            />
        </div>
    );
};

export default MedPassSafetyAlerts;